import {createRef, RefObject, useEffect, useMemo, useState} from "react";
import {useAppDispatch, useAppSelector} from "../../../app/hooks";
import {removeCartItem, StoreCart, useCart, useCurrency} from "../../../app/store/store";
import Footer from "../../../component/footer/Footer";
import Navbar from "../../../component/navbar/Navbar";
import fetchWithRetry, {withLanguage} from "../../../util/request";
import Currency, {formatCurrency} from "../currency/Currency";
import "./StoreCheckoutView.css";
import Title from "../../../component/title/Title";
import {IconGift, IconTicket, IconX} from "@tabler/icons-react";
import SwitchInput from "../../../component/switch/SwitchInput";
import Button from "../../../component/button/Button";
import {useAuth} from "../../../app/auth/auth";
import {Link, useNavigate} from "react-router-dom";
import Text from "../../../component/text/Text";
import {Icon} from "../../../component/icon/Icon";
import {totalForMonths} from "../item/modal/monthPricing";
import {Trans, useTranslation} from "react-i18next";
import {TFunction} from "i18next";

interface InputOptions {
    id: string
    label: string
    regex?: RegExp
    defaultValue?: string
    inputRef?: RefObject<HTMLInputElement>
}

function getInvoiceInputs(t: TFunction): InputOptions[] {
    return [
        {
            id: "fullname",
            label: t("checkout.inputs.fullname"),
            regex: /^\S+(\s\S+)*$/
        }, {
            id: "street",
            label: t("checkout.inputs.street"),
            regex: /^.+$/
        }, {
            id: "city",
            label: t("checkout.inputs.city"),
            regex: /^.+$/
        }, {
            id: "postalcode",
            label: t("checkout.inputs.postalcode"),
            regex: /^((\d{5})|(\d{3} \d{2}))$/
        }, {
            id: "country",
            label: t("checkout.inputs.country"),
            defaultValue: t("checkout.inputs.countryDefault"),
            regex: /^.+$/
        }
    ]
}

interface PaywallInfo {
    name: string
    id: string
    paywall: string
    icon: string
    enabled: boolean
    reason: string
}

interface CouponPreview {
    code: string
    discount_type: "NONE" | "PERCENT" | "FIXED"
    discount_value: number
    discount: number
    total: number
}

function InputField(props: InputOptions) {
    const {id, label, regex, defaultValue, inputRef} = props;

    let valid = true;
    const currentRef = inputRef?.current;
    if (currentRef && regex && currentRef.value.length > 0) {
        valid = regex.test(currentRef.value);
    }

    return <div className={`input-field${valid ? "" : " invalid"}`}>
        <label htmlFor={id} className={"label"}>{label}</label>
        <input type={"text"} name={id} defaultValue={defaultValue ?? ""}
               ref={inputRef} onInput={(e) => {
            const value = e.currentTarget.value;
            if (regex && !regex.test(value)) {
                e.currentTarget.classList.add("invalid")
            } else {
                e.currentTarget.classList.remove("invalid")
            }
        }}/>
    </div>
}

export default function StoreCheckoutView() {
    const {t, i18n} = useTranslation();
    const currencyName = useAppSelector(useCurrency)
    const cart: StoreCart = useAppSelector(useCart)
    const total = cart.items.reduce((sum, item) => {
        if (!item.store) return sum;
        return sum + totalForMonths(item.store, item.months ?? 1, currencyName);
    }, 0);
    const dispatch = useAppDispatch()
    const userToken = useAppSelector(useAuth).token
    const user = useAppSelector(useAuth).user

    const navigate = useNavigate()

    const [currencies, setCurrencies] = useState<{ [key: string]: Currency } | null>(null)

    const currency: Currency | undefined = currencies?.[currencyName]

    const [errorMessage, setErrorMessage] = useState<string | undefined>()

    const [invoice, setInvoice] = useState(false)
    const invoiceInputs = useMemo(() => getInvoiceInputs(t).map(input => {
        return {
            id: input.id,
            label: input.label,
            regex: input.regex,
            defaultValue: input.defaultValue,
            inputRef: createRef<HTMLInputElement>()
        }
    }), [t])
    const emailInput = {
        id: "email",
        label: t("checkout.inputs.email"),
        regex: /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/,
        inputRef: createRef<HTMLInputElement>()
    }
    const [vop, setVop] = useState(false)

    const [paymentOptions, setPaymentOptions] = useState<PaywallInfo[] | null>(null)
    const [selectedPaymentOption, setSelectedPaymentOption] =
        useState<null | {
            id: string
            paywall: string
        }>(null)

    const [couponInput, setCouponInput] = useState("")
    const [appliedCode, setAppliedCode] = useState<string | null>(null)
    const [coupon, setCoupon] = useState<CouponPreview | null>(null)
    const [couponError, setCouponError] = useState<string | null>(null)

    // Amount actually charged: the cart total minus the validated coupon discount (if any).
    const finalTotal = coupon ? coupon.total : total

    function applyCoupon() {
        const code = couponInput.trim().toUpperCase()
        if (!code) return;
        setCouponError(null)
        setAppliedCode(code)
    }

    function removeCoupon() {
        setAppliedCode(null)
        setCoupon(null)
        setCouponInput("")
        setCouponError(null)
    }

    useEffect(() => {
        return fetchWithRetry(async res => {
            if (!res.ok) return false;
            let body = (await res.json()) as { [key: string]: Currency };
            setCurrencies(body)
            return true;
        }, process.env.REACT_APP_API_BASE + "/store/currencies");
    }, [setCurrencies, i18n.language])

    useEffect(() => {
        if (!user) navigate("/login?return=/store/checkout")
    }, [user, navigate])

    // Validate the applied code and resolve its discount against the current cart total / currency.
    useEffect(() => {
        if (!appliedCode) {
            setCoupon(null)
            return;
        }
        return fetchWithRetry(async res => {
            if (res.status === 404) {
                setCoupon(null)
                setCouponError(t("checkout.errors.couponInvalid"))
                return true;
            }
            if (!res.ok) return false;
            setCoupon((await res.json()) as CouponPreview)
            setCouponError(null)
            return true;
        }, `${process.env.REACT_APP_API_BASE}/store/coupon/${encodeURIComponent(appliedCode)}?currency=${currencyName}&amount=${total}`);
    }, [appliedCode, currencyName, total, t])

    useEffect(() => {
        return fetchWithRetry(async res => {
            if (!res.ok) {
                setPaymentOptions(null)
                return false;
            }
            let body = (await res.json()) as PaywallInfo[]
            setPaymentOptions(body)
            return true;
        }, `${process.env.REACT_APP_API_BASE}/store/paywall/list/${currencyName}/${finalTotal}`);
    }, [setPaymentOptions, currencyName, finalTotal, i18n.language])

    const [sending, setSending] = useState(false);

    function sendForm() {
        if (!vop) {
            setErrorMessage(t("goal.errors.vopRequired"))
            return;
        }
        if (!selectedPaymentOption?.paywall) {
            setErrorMessage(t("goal.errors.paymentMethodRequired"));
            return;
        }
        setErrorMessage(undefined)
        if (sending) return;
        const inputs: InputOptions[] = [emailInput];
        if (invoice) inputs.push(...invoiceInputs);
        const form: { [key: string]: any } = {
            paywall: selectedPaymentOption.paywall
        }
        let invalid = false;
        for (let input of inputs) {
            const currentRef = input.inputRef?.current;
            if (input.regex && currentRef) {
                if (input.regex && !input.regex.test(currentRef.value)) {
                    invalid = true;
                    currentRef.classList.add("invalid")
                } else {
                    form[input.id] = currentRef.value ?? "";
                    currentRef.classList.remove("invalid")
                }
            }
        }
        if (invalid) return;
        form.invoice = invoice;

        const token = userToken;
        if (!token) return;

        setSending(true)

        const body = {
            items: cart.items.map(item => {
                return {
                    id: item.id,
                    category: item.category,
                    gift: item.gift ?? undefined,
                    months: item.months ?? undefined
                }
            }),
            currency: currencyName,
            form: form,
            coupon: coupon?.code ?? undefined
        };

        fetch(process.env.REACT_APP_API_BASE + "/store/checkout", withLanguage({
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": token,
                "Return-Hostname": window.location.origin
            },
            body: JSON.stringify(body)
        })).then(res => {
            setSending(false)
            if (res.ok) {
                res.text().then(url => {
                    window.localStorage.removeItem("pt-store-cart");
                    window.location.href = url;
                })
            } else {
                res.json().then(body => {
                    setErrorMessage(body.message);
                })
            }
        })
    }

    const invoiceRequired = (currency?.invoice_required_amount ?? finalTotal + 1) <= finalTotal

    if (invoiceRequired && !invoice) setInvoice(true)

    return <div className={"store-checkout-view"}>
        <Navbar/>
        {!currency ? <div className={"loader big center margin"}/> : <div className={"checkout"}>
            <Title>{t("checkout.title")}</Title>
            <div className={"items"}>
                {
                    cart.items.map((item, index) => {
                        if (!item.store) return null;
                        return <div className={"item"} key={index}>
                            <p className={"name"}>
                                <span className={"item-name"}>
                                    {item.store.name}
                                    {item.months && item.months > 1 ? (
                                        <span className={"months"}>{t("common.months", {count: item.months})}</span>
                                    ) : null}
                                </span>
                                {item.giftUser ? (
                                    <span className={"gift-tag"}>
                                        <IconGift/>
                                        <span className={"user"}>{item.giftUser.name}</span>
                                    </span>
                                ) : null}
                            </p>
                            <p className={"price"}>{formatCurrency(currency, totalForMonths(item.store, item.months ?? 1, currencyName))}</p>
                            <IconX size={30} className={"remove"} onClick={() => {
                                dispatch(removeCartItem(index))
                                if (cart.items.length === 1) {
                                    navigate("/store");
                                }
                                setPaymentOptions(null)
                            }}/>
                        </div>
                    })
                }
            </div>
            <div className={"coupon"}>
                <p className={"label"}>{t("checkout.couponLabel")}</p>
                {coupon ? (
                    <div className={"applied"}>
                        <span className={"code"}><IconTicket size={22}/> {coupon.code}</span>
                        <IconX size={24} className={"remove"} onClick={removeCoupon}/>
                    </div>
                ) : (
                    <div className={"coupon-input"}>
                        <input type={"text"} value={couponInput} placeholder={t("checkout.couponPlaceholder") as string}
                               onChange={e => setCouponInput(e.currentTarget.value)}
                               onKeyDown={e => {
                                   if (e.code === "Enter") {
                                       e.stopPropagation()
                                       applyCoupon()
                                   }
                               }}/>
                        <Button onClick={applyCoupon}>{t("checkout.apply")}</Button>
                    </div>
                )}
                {couponError ? <Text color={"red"}>{couponError}</Text> : null}
            </div>
            {coupon && coupon.discount > 0 ? (
                <div className={"summary discount"}>
                    <p className={"label"}>{t("checkout.discount")}</p>
                    <p className={"total"}>-{formatCurrency(currency, coupon.discount)}</p>
                </div>
            ) : null}
            <div className={"summary"}>
                <p className={"label"}>{t("common.total")}</p>
                <p className={"total"}>{formatCurrency(currency, finalTotal)}</p>
            </div>
            <div className={"form"} onKeyDown={e => {
                if (e.code === "Enter") sendForm()
            }}>
                <InputField {...emailInput}/>
                {invoiceRequired ? null :
                    <SwitchInput label={t("checkout.wantInvoice")} defaultValue={invoice} callback={setInvoice}/>}
                <div className={"invoice-requirements"} style={{
                    display: invoice ? undefined : "none"
                }}>
                    {invoiceInputs.map(input => <InputField key={input.id} {...input}/>)}
                </div>
                <SwitchInput label={<Trans i18nKey={"checkout.agreeVop"} components={{
                    link: <Link to={"/vop"} target={"_blank"}/>
                }}/>} defaultValue={vop} callback={setVop}/>
            </div>
            <div className={"payment-options"}>
                <p className={"label"}>{t("goal.paymentMethodLabel")}</p>
                {paymentOptions ? <div className={"paywall-list"}>
                    {paymentOptions.map(paywall => <div
                        key={paywall.id}
                        className={`paywall ${paywall.enabled ? "enabled" : "disabled"}${paywall.id === selectedPaymentOption?.id ? " selected" : ""}`}
                        onClick={(e) => {
                            if (paywall.enabled) {
                                setSelectedPaymentOption({
                                    id: paywall.id,
                                    paywall: paywall.paywall
                                })
                            }
                        }}>
                        <Icon iconName={paywall.icon}/>
                        <div className={"info"}>
                            <p className={"name"}>{paywall.name}</p>
                            {paywall.reason ? <p className={"reason"}>{paywall.reason}</p> : null}
                        </div>
                    </div>)}
                </div> : <div className={"loader center margin"}/>}
            </div>
            {errorMessage ? <Text color={"red"}>{errorMessage}</Text> : null}
            <Button onClick={sendForm} loading={sending}>{t("checkout.continueToPayment")}</Button>
        </div>}
        <Footer/>
    </div>
}